import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { alertLink, type DashboardAlert } from '@/utils/dashboard';
import { useAuth } from '@/context/AuthContext';
import { useRole } from '@/context/RoleContext';
import GlobalSearch from '@/components/feature/GlobalSearch';

interface TopbarProps {
  onMenuClick: () => void;
}

export default function Topbar({ onMenuClick }: TopbarProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { role, can } = useRole();
  const [openMenu, setOpenMenu] = useState<'user' | 'alerts' | null>(null);
  // The dashboard's alerts (GET /notifications), loaded once so the bell's dot shows only when there are some.
  const [alerts, setAlerts] = useState<DashboardAlert[] | null>(null);
  const [alertsError, setAlertsError] = useState(false);

  useEffect(() => {
    api<DashboardAlert[]>('/notifications')
      .then(setAlerts)
      .catch(() => {
        setAlerts([]);
        setAlertsError(true);
      });
  }, []);
  // "No active alerts" is a placeholder row, not an alert.
  const activeAlerts = (alerts ?? []).filter((alert) => alert.id !== 'al-clear');
  const initials = (user?.name ?? '')
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // A soft shadow once the page has scrolled under the bar.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggle = (menu: 'user' | 'alerts') =>
    setOpenMenu((current) => (current === menu ? null : menu));

  return (
    <header
      className={`relative flex h-16 items-center gap-3 border-b border-background-200 bg-background-50/85 px-4 backdrop-blur-md transition-shadow duration-300 md:px-6 ${
        scrolled ? 'topbar-scrolled' : ''
      }`}
    >
      <div className="brand-gradient pointer-events-none absolute inset-x-0 top-0 h-[3px]" aria-hidden="true" />
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Open menu"
        className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer lg:hidden"
      >
        <i className="ri-menu-line text-xl" />
      </button>

      <GlobalSearch />

      <div className="flex flex-1 items-center justify-end gap-2">
        {/* Notifications */}
        <div className="relative">
          <button
            type="button"
            onClick={() => toggle('alerts')}
            aria-label="Notifications"
            className="relative w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer"
          >
            <i className="ri-notification-3-line text-lg" />
            {activeAlerts.length > 0 ? (
              <span className="absolute right-1.5 top-1.5 w-2 h-2 rounded-full bg-primary-500" />
            ) : null}
          </button>
          {openMenu === 'alerts' ? (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setOpenMenu(null)}
                aria-hidden="true"
              />
              <div className="fixed inset-x-4 top-16 z-50 animate-menu-in max-h-[70vh] overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80">
                <p className="px-2 py-1.5 text-xs font-label font-semibold uppercase tracking-wider text-foreground-400">
                  Alerts
                </p>
                {alerts === null ? (
                  <p className="px-2 py-3 text-xs text-foreground-500">Loading alerts…</p>
                ) : null}
                {alertsError ? (
                  <p className="px-2 py-3 text-xs text-foreground-500">Couldn&apos;t load alerts.</p>
                ) : alerts?.length === 0 ? (
                  <p className="px-2 py-3 text-xs text-foreground-500">No alerts right now.</p>
                ) : null}
                {(alerts ?? []).map((alert) => {
                  const link = alertLink(alert, can);
                  return (
                  <button
                    key={alert.id}
                    type="button"
                    disabled={!link}
                    onClick={() => {
                      if (!link) return;
                      setOpenMenu(null);
                      navigate(link);
                    }}
                    className={`flex w-full gap-2 rounded-md px-2 py-2 text-left ${link ? 'cursor-pointer hover:bg-background-100' : 'cursor-default'}`}
                  >
                    <span
                      className={`w-7 h-7 flex items-center justify-center rounded-md text-base shrink-0 ${
                        alert.severity === 'high'
                          ? 'bg-primary-100 text-primary-700'
                          : alert.severity === 'medium'
                            ? 'bg-secondary-100 text-secondary-800'
                            : 'bg-accent-100 text-accent-700'
                      }`}
                    >
                      <i className={alert.icon} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground-900">
                        {alert.title}
                      </p>
                      <p className="text-[11px] text-foreground-500">{alert.detail}</p>
                    </div>
                    {link ? <i className="ri-arrow-right-s-line ml-auto self-center text-foreground-400" /> : null}
                  </button>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => toggle('user')}
            className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 py-1.5 pl-1.5 pr-2.5 cursor-pointer hover:bg-background-200"
          >
            {user?.avatar ? (
              <img
                src={user.avatar}
                decoding="async"
                alt={user.name}
                className="h-7 w-7 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-800">
                {initials}
              </span>
            )}
            <span className="hidden text-left sm:block">
              <span className="block text-xs font-medium leading-tight text-foreground-900">
                {user?.name}
              </span>
              <span className="block text-[10px] leading-tight text-foreground-500">
                {role.name}
              </span>
            </span>
            <i className="ri-arrow-down-s-line text-foreground-500" />
          </button>
          {openMenu === 'user' ? (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setOpenMenu(null)}
                aria-hidden="true"
              />
              <div className="absolute right-0 z-50 mt-2 w-64 animate-menu-in rounded-lg border border-background-200 bg-background-50 p-2">
                <div className="px-2 py-1.5">
                  <p className="text-xs font-medium text-foreground-900">{user?.name}</p>
                  <p className="truncate text-[11px] text-foreground-500">{user?.email}</p>
                </div>
                <div className="mt-1 border-t border-background-200 pt-1">
                  {can('roles.view') ? (
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenu(null);
                        navigate('/settings/roles');
                      }}
                      className="w-full rounded-md px-2 py-2 text-left text-xs font-medium text-foreground-800 cursor-pointer hover:bg-background-100"
                    >
                      Manage roles & permissions
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setOpenMenu(null);
                      navigate('/change-password');
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-medium text-foreground-800 cursor-pointer hover:bg-background-100"
                  >
                    <i className="ri-lock-password-line" />
                    Change password
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenMenu(null);
                      logout();
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-medium text-primary-700 cursor-pointer hover:bg-background-100"
                  >
                    <i className="ri-logout-box-r-line" />
                    Sign out
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}